import { getFirebaseBackend } from '../../authUtils.js'

export const state = {
    currentUser: sessionStorage.getItem('authUser'),
}

export const mutations = {
    SET_CURRENT_USER(state, newValue) {
        state.currentUser = newValue
        saveState('auth.currentUser', newValue)
    },
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.currentUser
    },
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ state, dispatch }) {
        dispatch('validate')
    },

    /**
     * Fija en el estado la sesión que ya existe en Firebase.
     *
     * El registro crea la cuenta directamente con el SDK (no pasa por `logIn`),
     * así que sin esto el estado `currentUser` seguía en null y el guard de
     * rutas rebotaba al recién registrado como si no tuviera sesión.
     */
    establecerUsuario({ commit }) {
        const user = getFirebaseBackend().getAuthenticatedUser()
        commit('SET_CURRENT_USER', user)
        return user
    },

    // Logs in the current user.
    //
    // Antes, si ya existía una sesión (`getters.loggedIn`), esto devolvía
    // `validate()` sin autenticar: quien escribía correo y contraseña de otra
    // cuenta entraba con la sesión anterior o no entraba en absoluto. Con dos
    // zonas en el mismo dominio (panel y portal) eso pasa constantemente, así
    // que un envío del formulario siempre debe autenticar de verdad.
    logIn({ commit }, { email, password } = {}) {
        return getFirebaseBackend().loginUser(email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });
    },

    /** Canjea con Firebase el token que entrega el botón de Google. */
    logInConCredencialGoogle({ commit }, idToken) {
        return getFirebaseBackend().loginWithGoogleCredential(idToken).then((usuario) => {
            commit('SET_CURRENT_USER', usuario)
            return usuario
        })
    },

    /** Métodos de acceso disponibles para un correo (contraseña, Google…). */
    metodosDeAcceso(_, email) {
        return getFirebaseBackend().metodosDeAcceso(email)
    },

    // Logs out the current user.
    logOut({ commit }) {
        commit('SET_CURRENT_USER', null)
        // El catch anterior llamaba a `this._handleError`, un método de la clase
        // de Firebase que aquí no existe: dentro de una acción de Vuex `this` es
        // el store. Cualquier fallo al cerrar sesión reventaba con un
        // "this._handleError is not a function" que enmascaraba el error real y
        // se mostraba al usuario como un fallo genérico de inicio de sesión.
        return getFirebaseBackend().logout()
            .then(() => true)
            .catch((error) => {
                console.error('[auth] error al cerrar sesión:', error)
                throw error
            })
    },

    // register the user
    register({ commit, dispatch, getters }, { email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().registerUser(email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });
    },

    // register the user
    // eslint-disable-next-line no-unused-vars
    resetPassword({ commit, dispatch, getters }, { email } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().forgetPassword(email).then((response) => {
            const message = response.data
            return message
        });
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ commit, state }) {
        if (!state.currentUser) return Promise.resolve(null)
        const user = getFirebaseBackend().getAuthenticatedUser();
        commit('SET_CURRENT_USER', user)
        return user;
    },
}

// ===
// Private helpers
// ===

function saveState(key, state) {
    window.localStorage.setItem(key, JSON.stringify(state))
}
